import { Affix, Button, Input, Select, Switch, message, Tooltip, Grid, Drawer, Modal, Checkbox, Tag, Space, Collapse, Row, Col, Dropdown, DatePicker } from "antd"
import { CloseOutlined, SendOutlined, DownOutlined } from '@ant-design/icons';
import { useClubSeasonManagementContext } from "./ClubSeasonManagement/sharedState";
import { useEffect, useState, useRef } from "react";
import { Colors } from "./ClubSeasonManagement/styles"
import { gen, swimminglyApi, RoleHelper } from "./utils";
import { StateMapper, useStoreState } from 'easy-peasy'
import { AppDataStore } from "../appData/types";
import { ISeasonDetails } from "../App"
import TiptapRichTextEditor from "./TiptapRichTextEditor"
import { sanitizeRichTextHtml } from "./utils/sanitizeRichTextHtml"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUpRightAndDownLeftFromCenter, faDownLeftAndUpRightToCenter, faAngleLeft, faEye } from "@fortawesome/pro-regular-svg-icons"


interface INewAnnouncementWindowProps{
    messageWindowVisible: boolean
    setMessageWindowVisible: (value: boolean) => void
}

const { useBreakpoint } = Grid

function isNonProdClientEnv(): boolean {
    if (typeof window === "undefined") return false
    const host = window.location.hostname || ""
    const isDev = host === "localhost" || host === "127.0.0.1"
    const isStaging = host.includes("swimminglytesting.ninja")
    return isDev || isStaging
}

type RecipientBase = {
    userId: number
    name: string
    email: string
    role: number
    roleName: string
    title?: string | null
    lastName?: string | null
}

type GuardianRecipient = RecipientBase & {
    swimmers?: {
        swimmerId: number
        firstName?: string | null
        lastName?: string | null
        displayName: string
        ageYears?: number | null
    }[]
    selected: boolean
}

type AdminRecipient = RecipientBase & {
    selected: boolean
}

export default function NewAnnouncementWindow({ 
    messageWindowVisible,
    setMessageWindowVisible
} : INewAnnouncementWindowProps){
    const screens = useBreakpoint()
    const isMobile = !screens.md
    const [isExpanded, setIsExpanded] = useState(false)
    const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
    const [announcement, setAnnouncement] = useState("")
    const [allowResponse, setAllowResponse] = useState(false)
    const [selectedSeason, setSelectedSeason] = useState<ISeasonDetails | undefined>()
    const [isSavingDraft, setIsSavingDraft] = useState(false)
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
    const [testOverrideEmail, setTestOverrideEmail] = useState("")
    const [showOverrideModal, setShowOverrideModal] = useState(false)
    const [recipientsLoading, setRecipientsLoading] = useState(false)
    const [drawerTargetingEnabled, setDrawerTargetingEnabled] = useState(true)
    const [clubhouseEmailMode, setClubhouseEmailMode] = useState<string | null>(
        null,
    )
    const [clubhouseOverrideAddress, setClubhouseOverrideAddress] = useState<
        string | null
    >(null)
    const [showOptOutsModal, setShowOptOutsModal] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [showScheduleModal, setShowScheduleModal] = useState(false)
    const [scheduleValue, setScheduleValue] = useState<any | null>(null)
    const [optOutsLoading, setOptOutsLoading] = useState(false)
    const [optOuts, setOptOuts] = useState<
        {
            userId: number
            name: string
            email: string
            role: number
            roleName?: string
            swimmers?: { swimmerId: number; displayName: string; ageYears?: number | null }[]
        }[]
    >([])
    const [optOutsCount, setOptOutsCount] = useState<number | null>(null)
    const [guardianRecipients, setGuardianRecipients] = useState<GuardianRecipient[]>([])
    const [clubAdminRecipients, setClubAdminRecipients] = useState<AdminRecipient[]>([])
    const [leagueAdminRecipients, setLeagueAdminRecipients] = useState<AdminRecipient[]>([])
    const [expandedRecipientKey, setExpandedRecipientKey] = useState<string | null>(null)
    const [activeSection, setActiveSection] = useState<"editor" | "recipients" | "sender" | null>("editor")
    const [mode, setMode] = useState<"edit" | "review">("edit")
    const saveTimeoutRef = useRef<number | null>(null)
    const nonProdClient = isNonProdClientEnv()
    
    const { loadUpcomingSeason, upcomingSeason, allUpcomingSeasons } = useClubSeasonManagementContext()
    
    useEffect(() => {
        loadUpcomingSeason()
    }, [loadUpcomingSeason])

    useEffect(() => {
        // Read-only config so Clubhouse drawer respects the global admin
        // toggle for granular targeting and can surface current email
        // behavior for non-production environments.
        swimminglyApi
            .get(gen("/api/messaging/clubhouse-announcement/config"))
            .then((resp: any) => {
                const data: any = (resp as any)?.data ?? resp ?? {}
                if (typeof data.clubhouseDrawerTargetingEnabled === "boolean") {
                    setDrawerTargetingEnabled(data.clubhouseDrawerTargetingEnabled)
                } else {
                    setDrawerTargetingEnabled(true)
                }
                if (typeof data.clubhouseAnnouncementEmailMode === "string") {
                    setClubhouseEmailMode(data.clubhouseAnnouncementEmailMode)
                } else {
                    setClubhouseEmailMode(null)
                }
                if (typeof data.clubhouseAnnouncementOverrideAddress === "string") {
                    setClubhouseOverrideAddress(
                        data.clubhouseAnnouncementOverrideAddress,
                    )
                } else {
                    setClubhouseOverrideAddress(null)
                }
            })
            .catch((err: any) => {
                console.error(
                    "NewAnnouncementWindow: failed to load drawer config",
                    err,
                )
                setDrawerTargetingEnabled(true)
            })
    }, [])
    
     
    useEffect(() => {
        setSelectedSeason(upcomingSeason)
    }, [upcomingSeason, allUpcomingSeasons])

    useEffect(() => {
        if (!messageWindowVisible) {
            setIsExpanded(false)
            setMode("edit")
        }
    }, [messageWindowVisible])

    const loadRecipientsForSeason = async (season: ISeasonDetails | undefined) => {
        if (!season || !user) {
            setGuardianRecipients([])
            setClubAdminRecipients([])
            setLeagueAdminRecipients([])
            return
        }

        try {
            setRecipientsLoading(true)
            setGuardianRecipients([])
            setClubAdminRecipients([])
            setLeagueAdminRecipients([])

            const resp = await swimminglyApi.get(
                gen(
                    `/api/messaging/clubhouse-announcement/recipients?clubId=${encodeURIComponent(
                        String(season.clubId),
                    )}&seasonId=${encodeURIComponent(String(season.seasonId))}`,
                ),
            )
            const data: any = (resp as any)?.data ?? resp ?? {}

            const guardiansRaw: any[] = Array.isArray(data.guardians)
                ? data.guardians
                : []
            const clubAdminsRaw: any[] = Array.isArray(data.clubAdmins)
                ? data.clubAdmins
                : []
            const leagueAdminsRaw: any[] = Array.isArray(data.leagueAdmins)
                ? data.leagueAdmins
                : []

            const guardiansMapped: GuardianRecipient[] = guardiansRaw.map((g) => ({
                userId: g.userId,
                name: g.name,
                email: g.email,
                role: g.role,
                roleName: g.roleName,
                title: g.title ?? null,
                lastName: g.lastName ?? null,
                swimmers: Array.isArray(g.swimmers)
                    ? g.swimmers.map((s: any) => ({
                          swimmerId: s.swimmerId,
                          firstName: s.firstName,
                          lastName: s.lastName,
                          displayName: s.displayName,
                          ageYears:
                              typeof s.ageYears === "number" && !Number.isNaN(s.ageYears)
                                  ? s.ageYears
                                  : undefined,
                      }))
                    : [],
                selected: g.defaultSelected !== false,
            }))

            const clubAdminsMapped: AdminRecipient[] = clubAdminsRaw.map((a) => ({
                userId: a.userId,
                name: a.name,
                email: a.email,
                role: a.role,
                roleName: a.roleName,
                title: a.title ?? null,
                lastName: a.lastName ?? null,
                selected: a.defaultSelected !== false,
            }))

            const leagueAdminsMapped: AdminRecipient[] = leagueAdminsRaw.map((a) => ({
                userId: a.userId,
                name: a.name,
                email: a.email,
                role: a.role,
                roleName: a.roleName,
                title: a.title ?? null,
                lastName: a.lastName ?? null,
                selected: a.defaultSelected !== false,
            }))

            setGuardianRecipients(guardiansMapped)
            setClubAdminRecipients(clubAdminsMapped)
            setLeagueAdminRecipients(leagueAdminsMapped)
        } catch (err) {
            console.error("NewAnnouncementWindow: failed to load recipients", err)
            const status = (err as any)?.response?.status
            if (status === 403) {
                message.error(
                    "You don't have permission to load recipients for this season.",
                )
            } else if (status === 400) {
                message.error(
                    "This club and season combination is not valid for announcements.",
                )
            } else {
                message.error("Failed to load announcement recipients")
            }
        } finally {
            setRecipientsLoading(false)
        }
    }

    useEffect(() => {
        if (!drawerTargetingEnabled) return
        if (!messageWindowVisible) return
        if (!selectedSeason || !user) return

        // Load recipients automatically for the currently selected season
        // when the drawer opens or the season changes.
        loadRecipientsForSeason(selectedSeason)
    }, [drawerTargetingEnabled, messageWindowVisible, selectedSeason, user])
    
    const handleChange = (option: any) => {
        const matchingSeason = allUpcomingSeasons.find(
            (season) => season.seasonId === option.value,
        )
        if (!matchingSeason) return

        if (typeof window !== "undefined" && user && selectedSeason) {
            const previousKey = `announcementDraft:${user.userId}:${selectedSeason.seasonId}`
            try {
                const sanitized = sanitizeRichTextHtml(announcement || "")
                const plain = sanitized.replace(/<[^>]+>/g, "").trim()
                if (plain.length === 0) {
                    window.localStorage.removeItem(previousKey)
                } else {
                    window.localStorage.setItem(previousKey, announcement)
                }
            } catch {
                // ignore storage errors
            }
        }

        setSelectedSeason(matchingSeason)
        loadRecipientsForSeason(matchingSeason)

        if (typeof window !== "undefined" && user) {
            const newKey = `announcementDraft:${user.userId}:${matchingSeason.seasonId}`
            try {
                const stored = window.localStorage.getItem(newKey)
                setAnnouncement(stored || "")
            } catch {
                setAnnouncement("")
            }
        } else {
            setAnnouncement("")
        }
    };

    const onChange = (value: string) => {
        setAnnouncement(value)
    };
    
    const options: { label: string; value: number }[] = (allUpcomingSeasons as ISeasonDetails[]).map(
        (season: ISeasonDetails) => ({
            label: season.seasonName,
            value: season.seasonId,
        }),
    )

    const newAnnouncementDefaultValue = selectedSeason?.seasonId ?
        { value: selectedSeason?.seasonId, label: selectedSeason?.seasonName }
        : undefined

    const sanitizedAnnouncement = sanitizeRichTextHtml(announcement || "")
    const plainTextAnnouncement = sanitizedAnnouncement.replace(/<[^>]+>/g, "").trim()
    const hasContent = plainTextAnnouncement.length > 0
    const hasSelectedRecipients =
        guardianRecipients.some((g) => g.selected) ||
        clubAdminRecipients.some((a) => a.selected) ||
        leagueAdminRecipients.some((a) => a.selected)
    const canSend = drawerTargetingEnabled
        ? Boolean(selectedSeason && hasContent && hasSelectedRecipients)
        : Boolean(selectedSeason && hasContent)

    useEffect(() => {
        if (!user || !selectedSeason) return
        if (typeof window === "undefined") return

        if (saveTimeoutRef.current !== null) {
            window.clearTimeout(saveTimeoutRef.current)
        }

        const key = `announcementDraft:${user.userId}:${selectedSeason.seasonId}`
        const content = announcement || ""
        const sanitized = sanitizeRichTextHtml(content)
        const plain = sanitized.replace(/<[^>]+>/g, "").trim()

        if (!plain.length) {
            try {
                window.localStorage.removeItem(key)
            } catch {
                // ignore storage errors
            }
            setIsSavingDraft(false)
            return
        }

        setIsSavingDraft(true)
        saveTimeoutRef.current = window.setTimeout(() => {
            try {
                window.localStorage.setItem(key, content)
            } catch {
                // ignore storage errors
            }
            setIsSavingDraft(false)
            const timeString = new Date().toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
            })
            setLastSavedAt(timeString)
        }, 1000)

        return () => {
            if (saveTimeoutRef.current !== null) {
                window.clearTimeout(saveTimeoutRef.current)
            }
        }
    }, [announcement, user, selectedSeason])

    useEffect(() => {
        if (!user || !selectedSeason) return
        if (typeof window === "undefined") return
        if (announcement) return

        const key = `announcementDraft:${user.userId}:${selectedSeason.seasonId}`
        try {
            const stored = window.localStorage.getItem(key)
            if (stored) {
                setAnnouncement(stored)
            }
        } catch {
            // ignore storage errors
        }
    }, [user, selectedSeason])

    type SendAnnouncementOptions = {
        overrideAddress?: string
        scheduledAt?: string | null
    }

    const sendAnnouncement = (options: SendAnnouncementOptions = {}) => {
        if (!user || !selectedSeason) return

        const { overrideAddress, scheduledAt } = options

        const selectedRecipientIds = drawerTargetingEnabled
            ? [
                  ...guardianRecipients
                      .filter((g) => g.selected)
                      .map((g) => g.userId),
                  ...clubAdminRecipients
                      .filter((a) => a.selected)
                      .map((a) => a.userId),
                  ...leagueAdminRecipients
                      .filter((a) => a.selected)
                      .map((a) => a.userId),
              ]
            : []

        if (drawerTargetingEnabled && selectedRecipientIds.length === 0) {
            message.error("Select at least one recipient for this announcement")
            return
        }

        const draftKey =
            user && selectedSeason
                ? `announcementDraft:${user.userId}:${selectedSeason.seasonId}`
                : null

        const scheduledAtIso = scheduledAt ?? null

        swimminglyApi
            .post(gen(`/api/messaging/createAnnouncement`))
            .body({
                clubId: selectedSeason?.clubId,
                seasonId: selectedSeason?.seasonId,
                clubName: selectedSeason?.clubName,
                userId: user?.userId,
                userName: user?.name,
                userEmail: user?.email,
                // Send sanitized rich text HTML so the email body can render
                // Tiptap formatting safely in the clubhouse announcement email.
                announcement: sanitizedAnnouncement,
                allowResponses: allowResponse,
                recipientUserIds: selectedRecipientIds,
                testOverrideToAddress: overrideAddress,
                scheduledAt: scheduledAtIso,
            })
            .then((data) => {
                if (data.status === "success") {
                    const totalEligible = typeof data.totalEligible === "number"
                        ? data.totalEligible
                        : undefined
                    const requestedCount = typeof data.requestedCount === "number"
                        ? data.requestedCount
                        : undefined
                    const actualLogical = typeof data.actualLogicalRecipients === "number"
                        ? data.actualLogicalRecipients
                        : undefined

                    const isScheduled = Boolean((data as any)?.scheduled)

                    if (isScheduled) {
                        const serverScheduledIso =
                            typeof (data as any)?.scheduledAt === "string"
                                ? (data as any).scheduledAt
                                : scheduledAtIso

                        const localDate = serverScheduledIso
                            ? new Date(serverScheduledIso)
                            : null

                        const localLabel = localDate
                            ? localDate.toLocaleString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                              })
                            : null

                        if (localLabel) {
                            message.success(
                                `Your announcement has been scheduled for ${localLabel}. Families and admins will see it in the Clubhouse communication drawer and receive email at that time.`,
                            )
                        } else {
                            message.success(
                                "Your announcement has been scheduled. Families and admins will see it in the Clubhouse communication drawer and receive email at the scheduled time.",
                            )
                        }
                    } else {
                        if (
                            requestedCount !== undefined &&
                            actualLogical !== undefined &&
                            requestedCount > actualLogical
                        ) {
                            message.warning(
                                `Your announcement was sent to ${actualLogical} of ${requestedCount} selected recipients based on their current eligibility and email preferences.`,
                            )
                        } else {
                            message.success(
                                "Your announcement has been posted to the Clubhouse communication drawer for all families & admins to view. They have also received an email notification with your announcement.",
                            )
                        }
                    }
                    if (draftKey && typeof window !== "undefined") {
                        try {
                            window.localStorage.removeItem(draftKey)
                        } catch {
                            // ignore storage errors
                        }
                    }
                    setSelectedSeason(undefined)
                    setAnnouncement("")
                    setAllowResponse(false)
                } else if (data.status === "error") {
                    const errMsg =
                        typeof (data as any)?.message === "string" && (data as any).message
                            ? (data as any).message
                            : "uh oh, something went wrong..."
                    message.error(errMsg)
                }
                return
            })
            .catch(() => {
                message.error("uh oh, something went wrong...")
                return
            })
            .finally(() => {
                setAllowResponse(false)
                setMessageWindowVisible(false)
            })
    }

    const scheduleAnnouncementAt = (scheduledDate: Date) => {
        const now = new Date()
        const minDate = new Date(now.getTime() + 5 * 60 * 1000)
        const maxDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

        if (scheduledDate < minDate) {
            message.error("Scheduled time must be at least 5 minutes from now.")
            return
        }

        if (scheduledDate > maxDate) {
            message.error("Scheduled time must be within the next 7 days.")
            return
        }
        const iso = scheduledDate.toISOString()
        sendAnnouncement({ scheduledAt: iso })
    }

    const loadOptOuts = async () => {
        if (!selectedSeason) return
        try {
            setOptOutsLoading(true)
            setOptOuts([])

            const resp = await swimminglyApi.get(
                gen(
                    `/api/messaging/clubhouse-announcement/opt-outs?clubId=${encodeURIComponent(
                        String(selectedSeason.clubId),
                    )}&seasonId=${encodeURIComponent(String(selectedSeason.seasonId))}`,
                ),
            )
            const data: any = (resp as any)?.data ?? resp ?? {}

            const users: any[] = Array.isArray(data.users) ? data.users : []
            const count: number | null =
                typeof data.count === "number" ? data.count : users.length || null

            setOptOuts(
                users.map((u) => ({
                    userId: u.userId,
                    name: u.name,
                    email: u.email,
                    role: typeof u.role === "number" ? u.role : undefined,
                    roleName: u.roleName,
                    swimmers: Array.isArray(u.swimmers) ? u.swimmers : [],
                })),
            )
            setOptOutsCount(count)
        } catch (err) {
            console.error("NewAnnouncementWindow: failed to load opt-outs", err)
            message.error("Unable to load opt-outs right now.")
        } finally {
            setOptOutsLoading(false)
        }
    }

    const renderContent = (expanded: boolean) => {
        const totalGuardians = guardianRecipients.length
        const totalClubAdmins = clubAdminRecipients.length
        const totalLeagueAdmins = leagueAdminRecipients.length

        const selectedGuardians = guardianRecipients.filter((g) => g.selected).length
        const selectedClubAdmins = clubAdminRecipients.filter((a) => a.selected).length
        const selectedLeagueAdmins = leagueAdminRecipients.filter((a) => a.selected).length

        const totalSelectedRecipients =
            selectedGuardians + selectedClubAdmins + selectedLeagueAdmins

        const isReviewMode = mode === "review"

        const headerRowBaseStyle: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            padding: "4px 6px",
            cursor: "pointer",
            borderRadius: 6,
            minHeight: 40,
        }

        const headerTextContainerStyle: React.CSSProperties = {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
        }

        const nameTextBaseStyle: React.CSSProperties = {
            fontSize: 14,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        }

        const emailTextStyle: React.CSSProperties = {
            fontSize: 12,
            color: "#888",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        }

        const footerActionsStyle: React.CSSProperties = {
            marginTop: 16,
            display: "flex",
            justifyContent: "flex-end",
        }

        const maxPreviewChars = 600
        const messagePreview = plainTextAnnouncement.length > maxPreviewChars
            ? `${plainTextAnnouncement.slice(0, maxPreviewChars).trimEnd()}…`
            : plainTextAnnouncement || "(No content)"

        return (
        <div
            style={{
                backgroundColor: "#c9d9e4",
                width: "100%",
                height: isMobile ? "100%" : undefined,
                maxHeight: !isMobile
                    ? expanded
                        ? "calc(100vh - 80px)"
                        : "70vh"
                    : undefined,
                padding: isMobile ? 16 : expanded ? 24 : 20,
                borderRadius: isMobile ? "20px 20px 0 0" : expanded ? 12 : 20,
                display: "flex",
                flexDirection: "column",
                boxShadow: isMobile
                    ? "0 -2px 8px rgba(0,0,0,0.15)"
                    : expanded
                        ? "0 4px 12px rgba(0,0,0,0.12)"
                        : "0 6px 16px rgba(0,0,0,0.15)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "3px solid black",
                    paddingBottom: 8,
                    marginBottom: 12,
                }}
            >
                <div
                    style={{
                        textAlign: "left",
                        marginLeft: 5,
                    }}
                >
                    <h4
                        style={{
                            marginBottom: 0,
                            fontSize: isMobile ? 16 : 18,
                        }}
                    >
                        Create Announcement
                    </h4>
                    <h5
                        style={{
                            marginTop: 4,
                            fontSize: isMobile ? 13 : 14,
                        }}
                    >
                        {selectedSeason?.clubName}
                    </h5>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        color: "black",
                    }}
                >
                    {!isMobile && !expanded && (
                        <span
                            style={{ cursor: "pointer" }}
                            onClick={() => setIsExpanded(true)}
                        >
                            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
                        </span>
                    )}
                    {!isMobile && expanded && (
                        <span
                            style={{ cursor: "pointer" }}
                            onClick={() => setIsExpanded(false)}
                        >
                            <FontAwesomeIcon icon={faDownLeftAndUpRightToCenter} />
                        </span>
                    )}
                    <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            setMessageWindowVisible(false)
                        }}
                    >
                        <CloseOutlined />
                    </span>
                </div>
            </div>
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        marginRight: isMobile ? -8 : 0,
                        paddingRight: isMobile ? 8 : 0,
                    }}
                >
                    {isReviewMode ? (
                        <div>
                            <div style={{ marginTop: 4 }}>
                                <h4
                                    style={{
                                        marginBottom: 4,
                                        fontSize: isMobile ? 14 : 16,
                                    }}
                                >
                                    Review and confirm
                                </h4>
                                <p
                                    style={{
                                        marginBottom: 8,
                                        fontSize: 12,
                                        color: "#595959",
                                    }}
                                >
                                    Double-check your message, recipients, and sender before posting.
                                </p>
                                {selectedSeason && (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "#595959",
                                            marginBottom: 4,
                                        }}
                                    >
                                        <strong>Season:</strong> {selectedSeason.seasonName}
                                    </div>
                                )}
                            </div>
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: 8,
                                    borderRadius: 8,
                                    border: "1px solid #d9d9d9",
                                    backgroundColor: "#ffffff",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        marginBottom: 4,
                                    }}
                                >
                                    <div style={{ fontWeight: 600 }}>Message</div>
                                    <Button
                                        size="small"
                                        type="link"
                                        icon={<FontAwesomeIcon icon={faEye} />}
                                        onClick={() => setShowPreviewModal(true)}
                                        disabled={!hasContent}
                                    >
                                        Preview
                                    </Button>
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#595959",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {messagePreview}
                                </div>
                            </div>
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: 8,
                                    borderRadius: 8,
                                    border: "1px solid #d9d9d9",
                                    backgroundColor: "#ffffff",
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Recipients</div>
                                {drawerTargetingEnabled ? (
                                    <div style={{ fontSize: 12, color: "#595959" }}>
                                        <div>
                                            Guardians: {selectedGuardians} of {totalGuardians}
                                        </div>
                                        <div>
                                            Club admins: {selectedClubAdmins} of {totalClubAdmins}
                                        </div>
                                        <div>
                                            League admins: {selectedLeagueAdmins} of {totalLeagueAdmins}
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <strong>Total selected recipients:</strong> {totalSelectedRecipients}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 12, color: "#595959" }}>
                                        This announcement will be posted and emailed to all eligible guardians and admins for this season.
                                    </div>
                                )}
                            </div>
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: 8,
                                    borderRadius: 8,
                                    border: "1px solid #d9d9d9",
                                    backgroundColor: "#ffffff",
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Sender</div>
                                <div style={{ fontSize: 12, color: "#595959" }}>
                                    <div>
                                        <strong>From:</strong>{" "}
                                        {user?.name || selectedSeason?.clubName || "Your club"}
                                    </div>
                                    <div style={{ marginTop: 2 }}>
                                        <strong>Replies:</strong>{" "}
                                        {allowResponse && user?.email
                                            ? `On (replies go to ${user.email})`
                                            : 'Off (sent from "Swimmingly" <no-reply@swimmingly.app>)'}
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: 8,
                                    borderRadius: 8,
                                    border: "1px solid #faad14",
                                    backgroundColor: "#fffbe6",
                                }}
                            >
                                <div
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: 4,
                                        color: "#d48806",
                                    }}
                                >
                                    Email delivery & opt-outs
                                </div>
                                <div style={{ fontSize: 12, color: "#595959" }}>
                                    This announcement will be posted in the Clubhouse communication drawer for all selected recipients.
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#595959",
                                        marginTop: 4,
                                    }}
                                >
                                    Some families may not receive an email if they’ve turned off Clubhouse announcement emails. They can opt back in at any time from their profile’s Email Preferences.
                                </div>
                                <div
                                    style={{
                                        marginTop: 8,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 12,
                                    }}
                                >
                                    <span>
                                        {optOutsCount !== null
                                            ? `${optOutsCount} recipient${
                                                  optOutsCount === 1 ? "" : "s"
                                              } currently opted out of email`
                                            : "View which recipients have opted out of email."}
                                    </span>
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                            setShowOptOutsModal(true)
                                            if (!optOutsCount && !optOutsLoading) {
                                                void loadOptOuts()
                                            }
                                        }}
                                        style={{ padding: 0 }}
                                    >
                                        View opt-outs
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Select
                                style={{ width: "100%" }}
                                defaultValue={newAnnouncementDefaultValue}
                                onSelect={handleChange}
                                options={options}
                                labelInValue={true}
                                value={options.find(
                                    (option) => option.value === selectedSeason?.seasonId,
                                )}
                            />
                            {/* Editor card: header + animated body */}
                            <div
                                style={{
                                    marginTop: 12,
                                    padding: 8,
                                    borderRadius: 8,
                                    border: "1px solid #d9d9d9",
                                    backgroundColor: "#ffffff",
                                }}
                            >
                                <Row
                                    align="middle"
                                    gutter={8}
                                    wrap={false}
                                    onClick={() =>
                                        setActiveSection((current) =>
                                            current === "editor" ? null : "editor",
                                        )
                                    }
                                >
                                    <Col flex="none">
                                        <span style={{ fontWeight: 600 }}>Message</span>
                                    </Col>
                                    <Col flex="auto">
                                        {activeSection !== "editor" && (
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: "#595959",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "block",
                                                }}
                                            >
                                                {plainTextAnnouncement || "Click to compose your announcement"}
                                            </span>
                                        )}
                                    </Col>
                                    <Col flex="none" style={{ textAlign: "right" }}>
                                        <FontAwesomeIcon
                                            icon={
                                                activeSection === "editor"
                                                    ? faDownLeftAndUpRightToCenter
                                                    : faUpRightAndDownLeftFromCenter
                                            }
                                        />
                                    </Col>
                                </Row>
                                <div
                                    style={{
                                        marginTop: 8,
                                        maxHeight: activeSection === "editor" ? 600 : 0,
                                        opacity: activeSection === "editor" ? 1 : 0,
                                        overflow: "hidden",
                                        transition: "max-height 0.2s ease, opacity 0.2s ease",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div
                                        style={{
                                            borderRadius: 4,
                                            border: "1px solid #d9d9d9",
                                            backgroundColor: "#ffffff",
                                        }}
                                    >
                                        <TiptapRichTextEditor
                                            value={announcement}
                                            onChange={onChange}
                                            placeholder="Your club's admins & guardians will see this in their communication drawer and receive an email with this announcement."
                                        />
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 4,
                                            fontSize: 12,
                                            color: "#595959",
                                        }}
                                    >
                                        <div>
                                            Announcements are emailed & posted to each recipient's communication drawer.
                                        </div>
                                        {lastSavedAt && (
                                            <div style={{ marginTop: 2, fontStyle: "italic" }}>
                                                {isSavingDraft ? "Saving draft..." : `Draft saved ${lastSavedAt}`}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {drawerTargetingEnabled && (
                                <div
                                    style={{
                                        marginTop: 16,
                                        padding: 8,
                                        borderRadius: 8,
                                        border: "1px solid #d9d9d9",
                                        backgroundColor: "#ffffff",
                                    }}
                                >
                                    <Row
                                        align="middle"
                                        gutter={8}
                                        wrap={false}
                                        style={{ marginBottom: 4 }}
                                        onClick={() =>
                                            setActiveSection((current) =>
                                                current === "recipients" ? null : "recipients",
                                            )
                                        }
                                    >
                                        <Col flex="none">
                                            <span style={{ fontWeight: 600 }}>Recipients</span>
                                        </Col>
                                        <Col flex="auto">
                                            {activeSection !== "recipients" && (
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#595959",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "block",
                                                    }}
                                                >
                                                    Guardians: {totalGuardians}, Club admins: {totalClubAdmins}, League admins: {totalLeagueAdmins}
                                                </span>
                                            )}
                                        </Col>
                                        <Col flex="none" style={{ textAlign: "right" }}>
                                            <FontAwesomeIcon
                                                icon={
                                                    activeSection === "recipients"
                                                        ? faDownLeftAndUpRightToCenter
                                                        : faUpRightAndDownLeftFromCenter
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    {recipientsLoading && (
                                        <div style={{ fontSize: 12 }}>Loading recipients…</div>
                                    )}
                                    <div
                                        style={{
                                            marginTop: 4,
                                            maxHeight:
                                                activeSection === "recipients"
                                                    ? isMobile
                                                        ? 460
                                                        : 520
                                                    : 0,
                                            opacity: activeSection === "recipients" ? 1 : 0,
                                            overflowY: activeSection === "recipients" ? "auto" : "hidden",
                                            transition: "max-height 0.2s ease, opacity 0.2s ease",
                                        }}
                                    >
                                        {!recipientsLoading && (
                                            <div>
                                                <div style={{ marginBottom: 8 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 4,
                                        }}
                                    >
                                        <span style={{ fontWeight: 500 }}>
                                            Club admins ({selectedClubAdmins} of {totalClubAdmins})
                                        </span>
                                        <span>
                                            <Button
                                                size="small"
                                                type="link"
                                                style={{ marginRight: 8 }}
                                                onClick={() =>
                                                    setClubAdminRecipients((prev) =>
                                                        prev.map((r) => ({ ...r, selected: true })),
                                                    )
                                                }
                                            >
                                                Select all
                                            </Button>
                                            <Button
                                                size="small"
                                                type="link"
                                                onClick={() =>
                                                    setClubAdminRecipients((prev) =>
                                                        prev.map((r) => ({ ...r, selected: false })),
                                                    )
                                                }
                                            >
                                                Select none
                                            </Button>
                                        </span>
                                    </div>
                                    {clubAdminRecipients.length === 0 ? (
                                        <div style={{ fontSize: 12, color: "#999" }}>
                                            No club admins for this season.
                                        </div>
                                    ) : (
                                        <Collapse
                                            accordion
                                            bordered={false}
                                            expandIconPlacement="end"
                                            activeKey={
                                                expandedRecipientKey &&
                                                expandedRecipientKey.startsWith("clubAdmin:")
                                                    ? expandedRecipientKey
                                                    : undefined
                                            }
                                            onChange={(key) => {
                                                const nextKey = Array.isArray(key)
                                                    ? key[0]
                                                    : (key as string | undefined)
                                                setExpandedRecipientKey(nextKey || null)
                                            }}
                                        >
                                            {clubAdminRecipients.map((a) => {
                                                const panelKey = `clubAdmin:${a.userId}`
                                                const isSelected = a.selected
                                                return (
                                                    <Collapse.Panel
                                                        key={panelKey}
                                                        header={
                                                            <div
                                                                style={{
                                                                    ...headerRowBaseStyle,
                                                                    alignItems: "flex-start",
                                                                    backgroundColor: isSelected
                                                                        ? "#e6f4ff"
                                                                        : "transparent",
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    style={{ marginRight: 8, marginTop: 2 }}
                                                                    checked={a.selected}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked
                                                                        setClubAdminRecipients((current) =>
                                                                            current.map((row) =>
                                                                                row.userId === a.userId
                                                                                    ? { ...row, selected: checked }
                                                                                    : row,
                                                                            ),
                                                                        )
                                                                    }}
                                                                />
                                                                <div style={headerTextContainerStyle}>
                                                                    <div
                                                                        style={{
                                                                            ...nameTextBaseStyle,
                                                                            fontWeight: isSelected ? 600 : 500,
                                                                        }}
                                                                    >
                                                                        {a.name}
                                                                    </div>
                                                                    <div style={emailTextStyle}>{a.email}</div>
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: "#666",
                                                                marginTop: 2,
                                                            }}
                                                        >
                                                            Role: {a.title || a.roleName}
                                                        </div>
                                                    </Collapse.Panel>
                                                )
                                            })}
                                        </Collapse>
                                    )}
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 4,
                                        }}
                                    >
                                        <span style={{ fontWeight: 500 }}>
                                            League admins ({selectedLeagueAdmins} of {totalLeagueAdmins})
                                        </span>
                                        <span>
                                            <Button
                                                size="small"
                                                type="link"
                                                style={{ marginRight: 8 }}
                                                onClick={() =>
                                                    setLeagueAdminRecipients((prev) =>
                                                        prev.map((r) => ({ ...r, selected: true })),
                                                    )
                                                }
                                            >
                                                Select all
                                            </Button>
                                            <Button
                                                size="small"
                                                type="link"
                                                onClick={() =>
                                                    setLeagueAdminRecipients((prev) =>
                                                        prev.map((r) => ({ ...r, selected: false })),
                                                    )
                                                }
                                            >
                                                Select none
                                            </Button>
                                        </span>
                                    </div>
                                    {leagueAdminRecipients.length === 0 ? (
                                        <div style={{ fontSize: 12, color: "#999" }}>
                                            No league admins for this season.
                                        </div>
                                    ) : (
                                        <Collapse
                                            accordion
                                            bordered={false}
                                            expandIconPlacement="end"
                                            activeKey={
                                                expandedRecipientKey &&
                                                expandedRecipientKey.startsWith("leagueAdmin:")
                                                    ? expandedRecipientKey
                                                    : undefined
                                            }
                                            onChange={(key) => {
                                                const nextKey = Array.isArray(key)
                                                    ? key[0]
                                                    : (key as string | undefined)
                                                setExpandedRecipientKey(nextKey || null)
                                            }}
                                        >
                                            {leagueAdminRecipients.map((a) => {
                                                const panelKey = `leagueAdmin:${a.userId}`
                                                const isSelected = a.selected
                                                return (
                                                    <Collapse.Panel
                                                        key={panelKey}
                                                        header={
                                                            <div
                                                                style={{
                                                                    ...headerRowBaseStyle,
                                                                    alignItems: "flex-start",
                                                                    backgroundColor: isSelected
                                                                        ? "#e6f4ff"
                                                                        : "transparent",
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    style={{ marginRight: 8, marginTop: 2 }}
                                                                    checked={a.selected}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked
                                                                        setLeagueAdminRecipients((current) =>
                                                                            current.map((row) =>
                                                                                row.userId === a.userId
                                                                                    ? { ...row, selected: checked }
                                                                                    : row,
                                                                            ),
                                                                        )
                                                                    }}
                                                                />
                                                                <div style={headerTextContainerStyle}>
                                                                    <div
                                                                        style={{
                                                                            ...nameTextBaseStyle,
                                                                            fontWeight: isSelected ? 600 : 500,
                                                                        }}
                                                                    >
                                                                        {a.name}
                                                                    </div>
                                                                    <div style={emailTextStyle}>{a.email}</div>
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: "#666",
                                                                marginTop: 2,
                                                            }}
                                                        >
                                                            Role: {a.title || a.roleName}
                                                        </div>
                                                    </Collapse.Panel>
                                                )
                                            })}
                                        </Collapse>
                                    )}
                                </div>

                                <div>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 4,
                                        }}
                                    >
                                        <span style={{ fontWeight: 500 }}>
                                            Guardians ({selectedGuardians} of {totalGuardians})
                                        </span>
                                        <span>
                                            <Button
                                                size="small"
                                                type="link"
                                                style={{ marginRight: 8 }}
                                                onClick={() =>
                                                    setGuardianRecipients((prev) =>
                                                        prev.map((r) => ({ ...r, selected: true })),
                                                    )
                                                }
                                            >
                                                Select all
                                            </Button>
                                            <Button
                                                size="small"
                                                type="link"
                                                onClick={() =>
                                                    setGuardianRecipients((prev) =>
                                                        prev.map((r) => ({ ...r, selected: false })),
                                                    )
                                                }
                                            >
                                                Select none
                                            </Button>
                                        </span>
                                    </div>
                                    {guardianRecipients.length === 0 ? (
                                        <div style={{ fontSize: 12, color: "#999" }}>
                                            No guardians for this season.
                                        </div>
                                    ) : (
                                        <Collapse
                                            accordion
                                            bordered={false}
                                            expandIconPlacement="end"
                                            activeKey={
                                                expandedRecipientKey &&
                                                expandedRecipientKey.startsWith("guardian:")
                                                    ? expandedRecipientKey
                                                    : undefined
                                            }
                                            onChange={(key) => {
                                                const nextKey = Array.isArray(key)
                                                    ? key[0]
                                                    : (key as string | undefined)
                                                setExpandedRecipientKey(nextKey || null)
                                            }}
                                        >
                                            {guardianRecipients.map((g) => {
                                                const panelKey = `guardian:${g.userId}`
                                                const isSelected = g.selected
                                                return (
                                                    <Collapse.Panel
                                                        key={panelKey}
                                                        header={
                                                            <div
                                                                style={{
                                                                    ...headerRowBaseStyle,
                                                                    alignItems: "flex-start",
                                                                    backgroundColor: isSelected
                                                                        ? "#e6f4ff"
                                                                        : "transparent",
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    style={{ marginRight: 8, marginTop: 2 }}
                                                                    checked={g.selected}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked
                                                                        setGuardianRecipients((current) =>
                                                                            current.map((row) =>
                                                                                row.userId === g.userId
                                                                                    ? { ...row, selected: checked }
                                                                                    : row,
                                                                            ),
                                                                        )
                                                                    }}
                                                                />
                                                                <div style={headerTextContainerStyle}>
                                                                    <div
                                                                        style={{
                                                                            ...nameTextBaseStyle,
                                                                            fontWeight: isSelected ? 600 : 500,
                                                                        }}
                                                                    >
                                                                        {g.name}
                                                                    </div>
                                                                    <div style={emailTextStyle}>{g.email}</div>
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <div style={{ marginTop: 4, paddingLeft: 32 }}>
                                                            {g.swimmers && g.swimmers.length > 0 ? (
                                                                <>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 12,
                                                                            marginBottom: 4,
                                                                        }}
                                                                    >
                                                                        Swimmers:
                                                                    </div>
                                                                    <Space size="small" wrap>
                                                                        {g.swimmers.map((s) => (
                                                                            <Tag key={s.swimmerId} color="blue">
                                                                                {s.displayName}
                                                                                {typeof s.ageYears === "number"
                                                                                    ? ` (${s.ageYears})`
                                                                                    : ""}
                                                                            </Tag>
                                                                        ))}
                                                                    </Space>
                                                                </>
                                                            ) : (
                                                                <span
                                                                    style={{
                                                                        fontSize: 11,
                                                                        color: "#999",
                                                                    }}
                                                                >
                                                                    No swimmers for this season
                                                                </span>
                                                            )}
                                                        </div>
                                                    </Collapse.Panel>
                                                )
                                            })}
                                        </Collapse>
                                    )}
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                )}
                {/* Sender card */}
                <div
                    style={{
                        marginTop: 16,
                        padding: 8,
                        borderRadius: 8,
                        border: "1px solid #d9d9d9",
                        backgroundColor: "#ffffff",
                    }}
                >
                    <Row
                        align="middle"
                        gutter={8}
                        wrap={false}
                        onClick={() =>
                            setActiveSection((current) =>
                                current === "sender" ? null : "sender",
                            )
                        }
                    >
                        <Col flex="none">
                            <span style={{ fontWeight: 600 }}>Sender</span>
                        </Col>
                        <Col flex="auto">
                            {activeSection !== "sender" && (
                                <span
                                    style={{
                                        fontSize: 12,
                                        color: "#595959",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "block",
                                    }}
                                >
                                    {allowResponse && user?.email
                                        ? `Replies to ${user.email}`
                                        : 'Replies off, "Swimmingly" <no-reply@swimmingly.app>'}
                                </span>
                            )}
                        </Col>
                        <Col flex="none" style={{ textAlign: "right" }}>
                            <FontAwesomeIcon
                                icon={
                                    activeSection === "sender"
                                        ? faDownLeftAndUpRightToCenter
                                        : faUpRightAndDownLeftFromCenter
                                }
                            />
                        </Col>
                    </Row>
                    <div
                        style={{
                            marginTop: 8,
                            maxHeight: activeSection === "sender" ? 200 : 0,
                            opacity: activeSection === "sender" ? 1 : 0,
                            overflow: "hidden",
                            transition: "max-height 0.2s ease, opacity 0.2s ease",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                color: "#595959",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: 4,
                                }}
                            >
                                {isMobile ? (
                                    <Switch
                                        defaultChecked={false}
                                        onChange={(checked) => setAllowResponse(checked)}
                                        style={{ marginRight: 8 }}
                                        checked={allowResponse}
                                    />
                                ) : (
                                    <Tooltip title="Receive email replies directly to your inbox, or route replies to a no-reply inbox. ">
                                        <Switch
                                            defaultChecked={false}
                                            onChange={(checked) => setAllowResponse(checked)}
                                            style={{ marginRight: 8 }}
                                            checked={allowResponse}
                                        />
                                    </Tooltip>
                                )}
                                <span>Allow replies</span>
                            </div>
                            <div style={{ marginBottom: 4 }}>
                                Sending from {allowResponse && user?.email ? user.email : '"Swimmingly" <no-reply@swimmingly.app>'}
                            </div>
                            <div>
                                When replies are on, families can reply directly to your email address. When replies are off, we send from the Swimmingly no-reply address and replies are not delivered.
                            </div>
                        </div>
                    </div>
                </div>
            </>
            )}
            </div>
            <div style={footerActionsStyle}>
                {mode === "edit" ? (
                    <>
                        <Button
                            shape="round"
                            onClick={() => {
                                setMessageWindowVisible(false)
                            }}
                        >
                            Cancel
                        </Button>
                        {isMobile ? (
                            <Button
                                shape="round"
                                disabled={!canSend}
                                onClick={() => {
                                    if (!canSend) {
                                        return
                                    }
                                    setMode("review")
                                }}
                                style={{
                                    marginLeft: 12,
                                    backgroundColor: canSend ? Colors.veryDarkBlue : "white",
                                    color: canSend ? "white" : "black",
                                }}
                            >
                                Post Announcement <SendOutlined />
                            </Button>
                        ) : (
                            <Tooltip title="Review this announcement before posting and emailing selected recipients.">
                                <Button
                                    shape="round"
                                    disabled={!canSend}
                                    onClick={() => {
                                        if (!canSend) {
                                            return
                                        }
                                        setMode("review")
                                    }}
                                    style={{
                                        marginLeft: 12,
                                        backgroundColor: canSend
                                            ? Colors.veryDarkBlue
                                            : "white",
                                        color: canSend ? "white" : "black",
                                    }}
                                >
                                    Post Announcement <SendOutlined />
                                </Button>
                            </Tooltip>
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            shape="round"
                            onClick={() => {
                                setMode("edit")
                            }}
                        >
                            <FontAwesomeIcon icon={faAngleLeft} /> Back
                        </Button>
                        {(() => {
                            const scheduleMenuItems = [
                                {
                                    key: "tomorrow-9am",
                                    label: "Send tomorrow at 9:00am",
                                },
                                {
                                    key: "custom",
                                    label: "Custom time…",
                                },
                            ]

                            const handleScheduleMenuClick = ({ key }: { key: string }) => {
                                if (!canSend) {
                                    return
                                }
                                if (key === "tomorrow-9am") {
                                    const now = new Date()
                                    const tomorrowAtNine = new Date(
                                        now.getFullYear(),
                                        now.getMonth(),
                                        now.getDate() + 1,
                                        9,
                                        0,
                                        0,
                                        0,
                                    )
                                    scheduleAnnouncementAt(tomorrowAtNine)
                                }
                                if (key === "custom") {
                                    setScheduleValue(null)
                                    setShowScheduleModal(true)
                                }
                            }

                            const scheduleDropdown = (
                                <Dropdown
                                    menu={{ items: scheduleMenuItems, onClick: handleScheduleMenuClick }}
                                >
                                    <Button
                                        shape="round"
                                        disabled={!canSend}
                                        style={{
                                            marginLeft: 8,
                                            backgroundColor: "white",
                                            color: canSend ? Colors.veryDarkBlue : "black",
                                            borderColor: Colors.veryDarkBlue,
                                        }}
                                    >
                                        Schedule <DownOutlined />
                                    </Button>
                                </Dropdown>
                            )

                            if (isMobile) {
                                return (
                                    <>
                                        <Button
                                            shape="round"
                                            disabled={!canSend}
                                            onClick={() => {
                                                if (!canSend) {
                                                    return
                                                }
                                                if (nonProdClient) {
                                                    setShowOverrideModal(true)
                                                } else {
                                                    sendAnnouncement()
                                                }
                                            }}
                                            style={{
                                                marginLeft: 12,
                                                backgroundColor: canSend
                                                    ? Colors.veryDarkBlue
                                                    : "white",
                                                color: canSend ? "white" : "black",
                                            }}
                                        >
                                            Send Now <SendOutlined />
                                        </Button>
                                        {scheduleDropdown}
                                    </>
                                )
                            }

                            return (
                                <>
                                    <Tooltip title="Send this announcement now to all selected recipients.">
                                        <Button
                                            shape="round"
                                            disabled={!canSend}
                                            onClick={() => {
                                                if (!canSend) {
                                                    return
                                                }
                                                if (nonProdClient) {
                                                    setShowOverrideModal(true)
                                                } else {
                                                    sendAnnouncement()
                                                }
                                            }}
                                            style={{
                                                marginLeft: 12,
                                                backgroundColor: canSend
                                                    ? Colors.veryDarkBlue
                                                    : "white",
                                                color: canSend ? "white" : "black",
                                            }}
                                        >
                                            Send Now <SendOutlined />
                                        </Button>
                                    </Tooltip>
                                    {scheduleDropdown}
                                </>
                            )
                        })()}
                    </>
                )}
            </div>
        </div>
        )
    }

    const optOutsModal = (
        <Modal
            open={showOptOutsModal}
            title="Email opt-outs for this season"
            footer={null}
            onCancel={() => setShowOptOutsModal(false)}
        >
            {optOutsLoading ? (
                <div style={{ fontSize: 12 }}>Loading opt-outs…</div>
            ) : optOuts.length === 0 ? (
                <div style={{ fontSize: 12 }}>
                    No recipients are currently opted out of Clubhouse announcement
                    emails for this season.
                </div>
            ) : (
                <div style={{ fontSize: 12 }}>
                    <div style={{ marginBottom: 8 }}>
                        {optOutsCount ?? optOuts.length} recipient
                        {optOutsCount === 1 ? " is" : "s are"} currently opted out
                        of email. They will still see this announcement in their
                        Clubhouse communication drawer.
                    </div>
                    <div
                        style={{
                            maxHeight: 260,
                            overflowY: "auto",
                            border: "1px solid #f0f0f0",
                            borderRadius: 4,
                            padding: 8,
                        }}
                    >
                        {optOuts.map((u) => {
                            const roleLabel =
                                typeof u.role === "number"
                                    ? new RoleHelper(u.role).roleName()
                                    : u.roleName || ""

                            const hasSwimmers =
                                Array.isArray(u.swimmers) && u.swimmers.length > 0

                            return (
                                <div
                                    key={u.userId}
                                    style={{
                                        padding: "4px 0",
                                        borderBottom: "1px solid #f5f5f5",
                                    }}
                                >
                                    <div style={{ fontWeight: 500 }}>{u.name}</div>
                                    <div style={{ color: "#595959" }}>{u.email}</div>
                                    {roleLabel && (
                                        <div style={{ color: "#8c8c8c" }}>{roleLabel}</div>
                                    )}
                                    {hasSwimmers && (
                                        <div style={{ marginTop: 4 }}>
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Swimmers:
                                            </div>
                                            <Space size="small" wrap>
                                                {u.swimmers!.map((s) => (
                                                    <Tag key={s.swimmerId} color="blue">
                                                        {s.displayName}
                                                        {typeof s.ageYears === "number"
                                                            ? ` (${s.ageYears})`
                                                            : ""}
                                                    </Tag>
                                                ))}
                                            </Space>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </Modal>
    )

    const previewModal = (
        <Modal
            open={showPreviewModal}
            title="Announcement preview"
            onCancel={() => setShowPreviewModal(false)}
            width={720}
            footer={[
                <Button
                    key="edit"
                    onClick={() => {
                        setShowPreviewModal(false)
                        setMode("edit")
                    }}
                >
                    Edit
                </Button>,
                <Button
                    key="close"
                    type="primary"
                    onClick={() => setShowPreviewModal(false)}
                >
                    Close
                </Button>,
            ]}
        >
            {hasContent ? (
                <div
                    style={{
                        maxHeight: 560,
                        overflowY: "auto",
                        padding: 16,
                        borderRadius: 12,
                        backgroundColor: "#eef4f7",
                    }}
                >
                    <div
                        style={{
                            marginBottom: 12,
                            fontSize: 12,
                            color: "#595959",
                        }}
                    >
                        This preview mirrors how the announcement appears in the Clubhouse communication drawer. Email styling may vary slightly by inbox.
                    </div>
                    <div
                        style={{
                            padding: 12,
                            borderRadius: 10,
                            backgroundColor: "#ffffff",
                            border: "1px solid #dbe5eb",
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                        }}
                    >
                        <div style={{ marginBottom: 8 }}>
                            <Tag color="#3c8825">Club Communication</Tag>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 12,
                                flexWrap: "wrap",
                                marginBottom: 10,
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "#1f1f1f",
                                    }}
                                >
                                    {user?.name || selectedSeason?.clubName || "Club Admin"}
                                </div>
                                <div
                                    style={{
                                        marginTop: 2,
                                        fontSize: 12,
                                        color: "#595959",
                                    }}
                                >
                                    {selectedSeason?.clubName || "Clubhouse announcement"}
                                </div>
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#8c8c8c",
                                    textAlign: "right",
                                }}
                            >
                                <div>Just now</div>
                                {selectedSeason?.seasonName && (
                                    <div style={{ marginTop: 2 }}>{selectedSeason.seasonName}</div>
                                )}
                            </div>
                        </div>
                        <div
                            style={{
                                borderTop: "1px solid #f0f0f0",
                                paddingTop: 12,
                                fontSize: 14,
                                lineHeight: 1.6,
                                color: "#262626",
                                wordBreak: "break-word",
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizedAnnouncement }}
                        />
                    </div>
                </div>
            ) : (
                <div style={{ fontSize: 12, color: "#595959" }}>
                    No announcement content to preview.
                </div>
            )}
        </Modal>
    )

    const scheduleModal = (
        <Modal
            open={showScheduleModal}
            title="Schedule announcement"
            okText="Schedule"
            cancelText="Cancel"
            onOk={() => {
                if (!scheduleValue) {
                    message.error("Choose a date and time for this announcement.")
                    return
                }

                const value: any = scheduleValue
                const scheduledDate: Date =
                    value && typeof value.toDate === "function"
                        ? value.toDate()
                        : value instanceof Date
                        ? value
                        : new Date(value)

                if (!scheduledDate || Number.isNaN(scheduledDate.getTime())) {
                    message.error("Please choose a valid date and time.")
                    return
                }

                scheduleAnnouncementAt(scheduledDate)
                setShowScheduleModal(false)
            }}
            onCancel={() => {
                setShowScheduleModal(false)
            }}
        >
            <p style={{ fontSize: 12, marginBottom: 8 }}>
                Choose when this announcement should be posted to the Clubhouse
                communication drawer and emailed to selected recipients.
            </p>
            <DatePicker
	            showTime={{ use12Hours: true, format: "h:mm a" }}
                style={{ width: "100%" }}
                value={scheduleValue as any}
                onChange={(value) => setScheduleValue(value)}
                disabledDate={(current: any) => {
                    if (!current) return false

                    const value: Date =
                        current && typeof current.toDate === "function"
                            ? current.toDate()
                            : current instanceof Date
                            ? current
                            : new Date(current)

                    if (!value || Number.isNaN(value.getTime())) {
                        return true
                    }

                    const now = new Date()
                    const today = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate(),
                    )
                    const maxDate = new Date(today)
                    maxDate.setDate(maxDate.getDate() + 7)

                    const candidate = new Date(
                        value.getFullYear(),
                        value.getMonth(),
                        value.getDate(),
                    )

                    return candidate < today || candidate > maxDate
                }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}>
                You can schedule announcements between 5 minutes and 7 days from
                now.
            </div>
        </Modal>
    )

    const overrideModal = nonProdClient ? (
        <Modal
            open={showOverrideModal}
            title="Send test announcement email"
            okText="Send announcement"
            cancelText="Cancel"
            onOk={() => {
                let overrideAddress: string | undefined
                if (testOverrideEmail.trim()) {
                    const trimmed = testOverrideEmail.trim()
                    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
                    if (!isValid) {
                        message.error("Please enter a valid email address.")
                        return
                    }
                    overrideAddress = trimmed
                }
                setShowOverrideModal(false)
                sendAnnouncement({ overrideAddress })
            }}
            onCancel={() => {
                setShowOverrideModal(false)
            }}
        >
            <p style={{ fontSize: 12, marginBottom: 8 }}>
                {(() => {
                    if (clubhouseEmailMode === "nonprod-override-single") {
                        const overrideLabel =
                            clubhouseOverrideAddress ||
                            "the configured override address"
                        return `Only ${overrideLabel} will receive a real email. All selected families and admins will still see this announcement in the Clubhouse drawer and appear in email logs.`
                    }
                    if (clubhouseEmailMode === "nonprod-real-all") {
                        return "Real emails will be sent to every selected, subscribed recipient in this non-production environment. Use this only when you intentionally want to email real users from staging or dev."
                    }
                    if (clubhouseEmailMode === "nonprod-override-disabled-no-address") {
                        return "No real emails will be sent because there is no override address configured and real recipient delivery is disabled server-side. This announcement will appear in the Clubhouse drawer and in email logs only."
                    }
                    return "This environment is configured as production; real emails will be sent to all selected, subscribed recipients."
                })()}
            </p>
            <Input
                placeholder="Optional test recipient email (non-production only)"
                value={testOverrideEmail}
                onChange={(e) => setTestOverrideEmail(e.target.value)}
            />
        </Modal>
    ) : null

    if (isMobile) {
        return (
            <>
                {optOutsModal}
                {previewModal}
                {scheduleModal}
                {overrideModal}
                <Drawer
                    placement="bottom"
                    open={messageWindowVisible}
                    onClose={() => setMessageWindowVisible(false)}
                    size="90vh"
                    closable={false}
                    styles={{
                        content: {
                            borderRadius: "20px 20px 0 0",
                            overflow: "hidden",
                            backgroundColor: "#c9d9e4",
                        },
                        body: {
                            padding: 0,
                            backgroundColor: "#c9d9e4",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        },
                    }}
                >
                    {renderContent(false)}
                </Drawer>
            </>
        )
    }

    return (
        <>
            {optOutsModal}
            {previewModal}
            {scheduleModal}
            {overrideModal}
            <Affix
                offsetBottom={0}
                style={{
                    position: "fixed",
                    right: 80,
                    bottom: 10,
                    zIndex: 1000,
                    visibility:
                        messageWindowVisible && !isExpanded ? "inherit" : "hidden",
                }}
            >
                <div
                    style={{
                        zIndex: 1000,
                        width: 440,
                        maxHeight: "70vh",
                    }}
                >
                    {renderContent(false)}
                </div>
            </Affix>
            <Modal
                open={isExpanded && messageWindowVisible}
                footer={null}
                closable={false}
                mask={{ closable: false }}
                width="100%"
                style={{ top: 0, paddingBottom: 0 }}
                styles={{ body: { padding: 0, backgroundColor: "#c9d9e4" } }}
                destroyOnHidden={false}
            >
                {renderContent(true)}
            </Modal>
        </>
    )
}